/**
 * Request header that drives Urban Sports Club (USC) flow behavior on the BFF.
 *
 * The header is the BFF's sole source of truth for the USC flow: when it is the literal string `'true'`, the BFF selects the USC offer-PDF template (both downloads) and publishes the USC tc-accepted event (the PATCH); any other value (or its absence) follows the standard Wellhub flow. `GET /v1/opportunity/:oppId` does not read it: `uscTcVersion` is always resolved there. The SPA decides whether to send it from the GrowthBook USC toggle (see `useUscWellhubFlow` / `WELLHUB_USC_FLOW_FLAG`).
 *
 * HTTP header names are case-insensitive; we send the Title-Case form
 * `Wellhub-USC-Flow`.
 */
const WELLHUB_USC_FLOW_HEADER = 'Wellhub-USC-Flow';

/**
 * Builds the `Wellhub-USC-Flow` header object to spread into a request's headers.
 *
 * Returns the header only when the USC flow is active. When inactive we omit it
 * entirely rather than sending `'false'`, matching the BFF contract (the value
 * is compared strictly against `'true'`).
 *
 * @param isUscFlow - Whether the current request is in the USC flow.
 * @returns `{ 'Wellhub-USC-Flow': 'true' }` when active, otherwise `{}`.
 */
export function resolveUSCFlowHeaders(
  isUscFlow?: boolean,
): Record<string, string> {
  return isUscFlow ? { [WELLHUB_USC_FLOW_HEADER]: 'true' } : {};
}

/**
 * Request header that drives Wellhub Club (WC) flow behavior on the BFF.
 *
 * The header is the BFF's sole source of truth for the Club flow. The SPA
 * decides whether to send it from the GrowthBook Club toggle (see
 * `useWellhubClub` / `useShowWellhubClub` / `WELLHUB_CLUB_FLAG`).
 *
 * HTTP header names are case-insensitive; we send the Title-Case form
 * `Wellhub-Club-Flow`.
 */
const WELLHUB_CLUB_FLOW_HEADER = 'Wellhub-Club-Flow';

/**
 * Builds the `Wellhub-Club-Flow` header object to spread into a request's headers.
 *
 * Returns the header only when the Club flow is active. When inactive we omit
 * it entirely rather than sending `'false'`, matching the BFF contract (the
 * value is compared strictly against `'true'`).
 *
 * @param isClubFlow - Whether the current request is in the Club flow.
 * @returns `{ 'Wellhub-Club-Flow': 'true' }` when active, otherwise `{}`.
 */
export function resolveClubFlowHeaders(
  isClubFlow?: boolean,
): Record<string, string> {
  return isClubFlow ? { [WELLHUB_CLUB_FLOW_HEADER]: 'true' } : {};
}
